/**
 * Application document helpers for position config and public posting forms.
 */
import type { ApplicationDocumentField, ApplicationDocumentFieldType, JobPosition } from '@/types';

export type { ApplicationDocumentField, ApplicationDocumentFieldType };

export const DOCUMENT_FIELD_TYPES: ApplicationDocumentFieldType[] = [
  'resume',
  'presentation_letter',
  'portfolio',
];

const MAX_DOCUMENT_FIELDS = 3;

export const DEFAULT_DOCUMENT_FIELDS: ApplicationDocumentField[] = [
  { type: 'resume', required: true },
];

/** Maps legacy positions without documents[] to resume-required. */
export function resolveDocumentFieldsFromPosition(raw: {
  documents?: ApplicationDocumentField[];
  applicationFields?: {
    documents?: ApplicationDocumentField[];
  };
}): ApplicationDocumentField[] {
  const fromFields = raw.applicationFields?.documents ?? raw.documents;
  if (fromFields?.length) return fromFields;
  return [...DEFAULT_DOCUMENT_FIELDS];
}

export function canAddDocumentField(fields: ApplicationDocumentField[]): boolean {
  return fields.length < MAX_DOCUMENT_FIELDS;
}

export function availableDocumentFieldTypes(
  fields: ApplicationDocumentField[],
): ApplicationDocumentFieldType[] {
  const used = new Set(fields.map((f) => f.type));
  return DOCUMENT_FIELD_TYPES.filter((t) => !used.has(t));
}

export function defaultDocumentField(type: ApplicationDocumentFieldType): ApplicationDocumentField {
  return { type, required: type === 'resume' };
}

export function documentFieldLabel(
  field: ApplicationDocumentField,
  t: (key: string) => string,
): string {
  return t(`positions.documentType.${field.type}`);
}

export function multipartFieldNameForDocumentType(type: ApplicationDocumentFieldType): string {
  switch (type) {
    case 'resume':
      return 'cv';
    case 'presentation_letter':
      return 'document_presentation_letter';
    case 'portfolio':
      return 'document_portfolio';
  }
}

export function isDocumentConfigured(
  position: Pick<JobPosition, 'documents'>,
  type: ApplicationDocumentFieldType,
): boolean {
  return (position.documents ?? DEFAULT_DOCUMENT_FIELDS).some((d) => d.type === type);
}

export function isDocumentRequired(
  position: Pick<JobPosition, 'documents'>,
  type: ApplicationDocumentFieldType,
): boolean {
  return (position.documents ?? DEFAULT_DOCUMENT_FIELDS).some(
    (d) => d.type === type && d.required,
  );
}

/** Firestore `documents/` type for candidate uploads. */
export const UPLOAD_DOCUMENT_TYPES: Record<ApplicationDocumentFieldType, string> = {
  resume: 'resume_document',
  presentation_letter: 'presentation_letter_document',
  portfolio: 'portfolio_document',
};

export function shouldShowApplicationDocumentTab(
  configDocuments: ApplicationDocumentField[],
  uploadedDocuments: { type: string }[] | null | undefined,
  docType: ApplicationDocumentFieldType,
): boolean {
  const inConfig = configDocuments.some((d) => d.type === docType);
  const uploaded = uploadedDocuments?.some((d) => d.type === UPLOAD_DOCUMENT_TYPES[docType]);
  return inConfig || !!uploaded;
}
